import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refreshSession } from '@/api/client';
import { openAgentSocket, type AgentSocket } from '@/api/realtime';
import { ticketsKey } from '@/api/tickets';
import { RealtimeContext } from './realtime-context';

/** A reply fires several events (message, status, assignee); refetch lists once for the burst. */
const LIST_REFRESH_DEBOUNCE_MS = 400;
/** Re-auth attempts after the server rejects the handshake (usually an expired access cookie). */
const MAX_REAUTH_ATTEMPTS = 5;

/** One dashboard socket per open workspace; screens subscribe through `useLiveTicket`. */
export function RealtimeProvider({ workspaceId, children }: { workspaceId: string; children: ReactNode }) {
  const qc = useQueryClient();
  const [socket, setSocket] = useState<AgentSocket | null>(null);

  useEffect(() => {
    const s = openAgentSocket(workspaceId);
    let listTimer: ReturnType<typeof setTimeout> | undefined;
    let reauthTimer: ReturnType<typeof setTimeout> | undefined;
    let reauthAttempts = 0;
    let connectedBefore = false;
    let closed = false;

    const refreshLists = () => {
      clearTimeout(listTimer);
      listTimer = setTimeout(() => {
        void qc.invalidateQueries({ queryKey: [...ticketsKey, 'list'] });
        void qc.invalidateQueries({ queryKey: [...ticketsKey, 'counts'] });
      }, LIST_REFRESH_DEBOUNCE_MS);
    };

    s.on('connect', () => {
      reauthAttempts = 0;
      // Back after a drop: catch up once on whatever changed while offline.
      if (connectedBefore) void qc.invalidateQueries({ queryKey: ticketsKey });
      connectedBefore = true;
    });
    s.on('tickets:changed', refreshLists);
    const reauth = (delayMs: number) => {
      if (reauthAttempts >= MAX_REAUTH_ATTEMPTS) return;
      reauthAttempts += 1;
      void refreshSession().then((ok) => {
        if (ok && !closed) reauthTimer = setTimeout(() => s.connect(), delayMs);
      });
    };
    s.on('connect_error', () => {
      // Transport errors are retried by socket.io itself; a rejected handshake is not.
      if (!s.active) reauth(1_000 * (reauthAttempts + 1));
    });
    s.on('disconnect', (reason) => {
      // The server ends a socket when its login expires: renew the session and reconnect, which
      // re-checks the account and company. If the session can't be renewed, live updates stop.
      if (reason === 'io server disconnect' && !closed) reauth(0);
    });

    setSocket(s);
    return () => {
      closed = true;
      clearTimeout(listTimer);
      clearTimeout(reauthTimer);
      s.disconnect();
      setSocket(null);
    };
  }, [qc, workspaceId]);

  return <RealtimeContext.Provider value={socket}>{children}</RealtimeContext.Provider>;
}
